import { z } from "zod";
import { approvalStateSchema } from "./states";

/**
 * One approval, as the inbox and the decision page read it. Bound to the action digest and the
 * revision the requester reviewed: if either has moved, the decider sees what changed before
 * anything happens. Eligibility is computed server-side, never by the page.
 */
export const approvalSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  runId: z.uuid().nullable(),
  title: z.string().min(1),
  actionType: z.string().min(1),
  actionDigest: z.string().min(1),
  revision: z.number().int().min(1),
  requestedBy: z.string().min(1),
  requestedById: z.uuid(),
  status: approvalStateSchema,
  canDecide: z.boolean(),
  decidedBy: z.string().nullable(),
  reason: z.string().nullable(),
  expiresAt: z.iso.datetime().nullable(),
  requestedAt: z.iso.datetime(),
});
export type Approval = z.infer<typeof approvalSchema>;

/**
 * `GET /api/v1/projects/:id/approvals`: waiting for the caller, waiting for others, decided.
 * Three tabs, as the Approvals board draws them.
 */
export const approvalsInboxSchema = z.object({
  waitingForMe: z.array(approvalSchema),
  waitingForOthers: z.array(approvalSchema),
  decided: z.array(approvalSchema),
});
export type ApprovalsInbox = z.infer<typeof approvalsInboxSchema>;

/**
 * `POST /api/v1/projects/:id/approvals/:id/decide`. Carries the digest and revision the caller
 * reviewed: a stale pair answers 409 with what moved and records nothing. A rejection requires
 * a reason; the author of the change is refused with 403.
 */
export const decideApprovalSchema = z.object({
  decision: z.enum(["approved", "declined"]),
  actionDigest: z.string().min(1),
  revision: z.number().int().min(1),
  reason: z.string().trim().max(2000).optional(),
});
export type DecideApproval = z.infer<typeof decideApprovalSchema>;
